import type { RouteRecordRaw } from 'vue-router'
import DefaultLayout from '@/components/layout/DefaultLayout.vue'
import AuthLayout from '@/components/layout/AuthLayout.vue'

export const routes: RouteRecordRaw[] = [
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        path: 'login',
        name: 'Login',
        component: () => import('@/features/auth/views/LoginView.vue'),
        meta: { title: 'Login', requiresAuth: false },
      },
      {
        path: 'register',
        name: 'Register',
        component: () => import('@/features/auth/views/RegisterView.vue'),
        meta: { title: 'Register', requiresAuth: false },
      },
      {
        path: 'forgot-password',
        name: 'ForgotPassword',
        component: () => import('@/features/auth/views/ForgotPasswordView.vue'),
        meta: { title: 'Forgot Password', requiresAuth: false },
      },
    ],
  },
  {
    // DEV-7217: public, unauthenticated "Request removal from TAMI" form.
    // A distinct path from the legacy /Account/PrivacyRequestRemoval cshtml
    // (which the Vite proxy still serves), so the two never collide.
    path: '/privacy/request-removal',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'PrivacyRequestRemoval',
        component: () => import('@/features/gdpr/views/PrivacyRequestRemovalView.vue'),
        meta: { title: 'Request Data Removal', requiresAuth: false },
      },
    ],
  },
  {
    // DEV-7227: public, unauthenticated Company Preview page. Uses AuthLayout
    // (a bare v-app/v-main shell) and calls the anonymous FO API, gated by
    // reCAPTCHA v3 — no logged-in user required.
    path: '/preview',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'CompanyPreview',
        component: () => import('@/features/companypreview/views/CompanyPreviewView.vue'),
        meta: { title: 'Company Preview', requiresAuth: false },
      },
    ],
  },
  {
    // First-login onboarding questionnaire. Rendered in the bare AuthLayout
    // (no sidebar/top-bar) for a focused, full-screen experience. Requires auth;
    // the router guard redirects new users here until they save a response.
    path: '/onboarding/questionnaire',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'OnboardingQuestionnaire',
        component: () => import('@/features/onboarding/views/QuestionnaireView.vue'),
        meta: { title: 'Getting Started', requiresAuth: true },
      },
    ],
  },
  {
    // New-UI private beta: shown when ENABLE_VUE3_NEW_UI is off (experience.guard.ts).
    // Bare AuthLayout so no app shell or data calls render behind it.
    path: '/beta-access',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'BetaAccess',
        component: () => import('@/features/experience/views/BetaAccessView.vue'),
        meta: { title: 'New TAMI beta', requiresAuth: true },
      },
    ],
  },
  {
    path: '/',
    component: DefaultLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'Dashboard',
        component: () => import('@/features/dashboard/views/DashboardView.vue'),
        meta: { title: 'Dashboard' },
      },
      {
        path: 'listbuilder',
        name: 'ListBuilder',
        component: () => import('@/features/listbuilder/views/ListBuilderView.vue'),
        meta: { title: 'List Builder' },
      },
      {
        path: 'people-search',
        name: 'PeopleSearch',
        component: () => import('@/features/peoplesearch/views/PeopleSearchView.vue'),
        meta: { title: 'People Search', blockedByDatapoint: 'HIDE_PEOPLE_SEARCH' },
      },
      {
        path: 'companydetail/:id',
        name: 'CompanyDetail',
        component: () => import('@/features/companydetail/views/CompanyDetailView.vue'),
        meta: { title: 'Company Detail' },
      },
      {
        path: 'onboarding',
        name: 'Onboarding',
        component: () => import('@/features/onboarding/views/OnboardingView.vue'),
        meta: { title: 'Getting Started', hideAiFab: true },
      },
      {
        path: 'my-lists',
        name: 'MyLists',
        component: () => import('@/features/mylists/views/MyListsView.vue'),
        meta: { title: 'My Lists' },
      },
      {
        path: 'my-lists/contacts/:id',
        name: 'UserListContactDetail',
        component: () => import('@/features/mylists/views/UserListContactDetailView.vue'),
        meta: { title: 'Contact List Detail' },
        props: true,
      },
      {
        path: 'my-lists/:id',
        name: 'UserListCompanyDetail',
        component: () => import('@/features/mylists/views/UserListCompanyDetailView.vue'),
        meta: { title: 'List Detail' },
        props: true,
      },
      {
        path: 'lead-enrichment',
        name: 'LeadEnrichment',
        component: () => import('@/features/leadenrichment/views/LeadEnrichmentView.vue'),
        meta: { title: 'Lead Enrichment' },
      },
      {
        path: 'lead-scoring',
        name: 'LeadScoring',
        component: () => import('@/features/leadscoring/views/LeadScoringListView.vue'),
        meta: { title: 'Lead Scoring' },
      },
      {
        path: 'lookalike',
        name: 'LookAlike',
        component: () => import('@/features/lookalike/views/LookAlikeView.vue'),
        meta: { title: 'LookAlike' },
      },
      {
        path: 'profile',
        name: 'Profile',
        component: () => import('@/features/user/views/MemberProfileView.vue'),
        meta: { title: 'Profile' },
      },
      {
        path: 'settings/change-password',
        name: 'ChangePassword',
        component: () => import('@/features/changepassword/views/ChangePasswordView.vue'),
        meta: { title: 'Change Password' },
      },
      {
        // Static: the setup and FAQ of tami.ai/mcp (config/mcp-connector.ts).
        path: 'mcp-connector',
        name: 'McpConnector',
        component: () => import('@/features/mcpconnector/views/McpConnectorView.vue'),
        meta: { title: 'MCP Connector' },
      },
      {
        path: 'icp',
        name: 'IcpList',
        component: () => import('@/features/icp/views/IcpListView.vue'),
        meta: { title: 'ICP Setup' },
      },
      {
        path: 'icp/new',
        name: 'IcpCreate',
        component: () => import('@/features/icp/views/IcpDetailView.vue'),
        meta: { title: 'Create ICP' },
      },
      {
        path: 'icp/:id',
        name: 'IcpEdit',
        component: () => import('@/features/icp/views/IcpDetailView.vue'),
        meta: { title: 'Edit ICP' },
        props: true,
      },
      {
        path: 'purchase-history/leads-purchased',
        name: 'LeadsPurchased',
        component: () => import('@/features/purchasehistory/views/PurchaseHistoryView.vue'),
        meta: { title: 'Leads Purchased' },
      },
      {
        path: 'purchase-history/ignored-leads',
        name: 'IgnoredLeads',
        component: () => import('@/features/purchasehistory/views/IgnoredLeadsView.vue'),
        meta: { title: 'Ignored Leads' },
      },
      {
        path: 'purchase-history/bulk-downloads',
        name: 'BulkDownloads',
        component: () => import('@/features/purchasehistory/views/BulkDownloadsView.vue'),
        meta: { title: 'Bulk Downloads' },
      },
      {
        path: 'purchase-history/bulk-download/:id',
        name: 'BulkDownloadDetail',
        component: () => import('@/features/purchasehistory/views/BulkDownloadDetailView.vue'),
        meta: { title: 'Bulk Download Detail' },
        props: true,
      },
      {
        path: 'v2/bulkdownload/:id',
        redirect: (to) => ({ name: 'BulkDownloadDetail', params: { id: to.params.id } }),
      },
      // The links in bell notifications and completion emails point at these Vue 2 pages.
      {
        path: 'v2/userlistdetail/:id',
        redirect: (to) => ({ name: 'UserListContactDetail', params: { id: to.params.id } }),
      },
      {
        path: 'v2/userlistcompanydetail/:id',
        redirect: (to) => ({ name: 'UserListCompanyDetail', params: { id: to.params.id } }),
      },
      {
        path: 'v2/leadenrichment',
        redirect: '/lead-enrichment',
      },
      {
        path: 'v2/leadscoringlist',
        redirect: '/lead-scoring',
      },
      {
        path: 'settings/api-access',
        name: 'ApiAccess',
        component: () => import('@/features/apiaccess/views/ApiAccessView.vue'),
        meta: { title: 'API Access' },
      },
      {
        path: 'settings/api-signup',
        name: 'ApiSignup',
        component: () => import('@/features/apisignup/views/ApiSignupView.vue'),
        meta: { title: 'Get a Free API Key' },
      },
      {
        path: 'settings/crm-agreement/:crmSlug',
        name: 'CrmAgreement',
        component: () => import('@/features/crmsetup/views/CrmAgreementView.vue'),
        meta: { title: 'CRM Agreement' },
        props: true,
      },
      {
        path: 'settings/crm-connect/closeio',
        name: 'CloseioConnect',
        component: () => import('@/features/crmsetup/views/CloseioConnectView.vue'),
        meta: { title: 'Connect CloseIO' },
      },
      {
        path: 'settings/crm-connect/webcrm',
        name: 'WebcrmConnect',
        component: () => import('@/features/crmsetup/views/WebcrmConnectView.vue'),
        meta: { title: 'Connect webCRM' },
      },
      {
        path: 'settings/crm-setup',
        name: 'CrmSetup',
        component: () => import('@/features/crmsetup/views/CrmSetupView.vue'),
        meta: { title: 'CRM Setup' },
      },
      {
        path: 'settings/salesforce-mapping',
        name: 'SalesforceMapping',
        component: () => import('@/features/salesforcemapping/views/SalesforceMappingView.vue'),
        meta: { title: 'Salesforce Mapping' },
      },
      {
        path: 'settings/salesforce-refresh',
        name: 'SalesforceRefresh',
        component: () => import('@/features/salesforcerefresh/views/SalesforceRefreshView.vue'),
        meta: { title: 'Salesforce Refresh' },
      },
      {
        path: 'settings/salesforce-refresh/setup/:objectName',
        name: 'SalesforceRefreshSetup',
        component: () => import('@/features/salesforcerefresh/views/SalesforceRefreshSetupView.vue'),
        meta: { title: 'Salesforce Refresh Setup' },
        props: true,
      },
      {
        path: 'settings/salesforce-refresh/company-data',
        name: 'SalesforceRefreshCompanyData',
        component: () => import('@/features/salesforcerefresh/views/SalesforceRefreshCompanyDataView.vue'),
        meta: { title: 'Salesforce Refresh - Company Data' },
      },
      {
        path: 'settings/salesforce-refresh/contact-data',
        name: 'SalesforceRefreshContactData',
        component: () => import('@/features/salesforcerefresh/views/SalesforceRefreshContactDataView.vue'),
        meta: { title: 'Salesforce Refresh - Contact Data' },
      },
      {
        path: 'settings/hubspot-mapping',
        name: 'HubspotMapping',
        component: () => import('@/features/hubspotmapping/views/HubspotMappingView.vue'),
        meta: { title: 'Hubspot Mapping' },
      },
      {
        path: 'settings/dynamics-mapping',
        name: 'DynamicsMapping',
        component: () => import('@/features/dynamicsmapping/views/DynamicsMappingView.vue'),
        meta: { title: 'Dynamics Mapping' },
      },
      {
        path: 'settings/zoho-mapping',
        name: 'ZohoMapping',
        component: () => import('@/features/zohomapping/views/ZohoMappingView.vue'),
        meta: { title: 'Zoho Mapping' },
      },
      {
        path: 'settings/salesloft-mapping',
        name: 'SalesloftMapping',
        component: () => import('@/features/salesloftmapping/views/SalesloftMappingView.vue'),
        meta: { title: 'Salesloft Mapping' },
      },
      {
        path: 'settings/pardot-mapping',
        name: 'PardotMapping',
        component: () => import('@/features/pardotmapping/views/PardotMappingView.vue'),
        meta: { title: 'Pardot Mapping' },
      },
      {
        path: 'settings/:crmName/industry-mapping',
        name: 'IndustryMapping',
        component: () => import('@/features/industrymapping/views/IndustryMappingView.vue'),
        meta: { title: 'Industry Mapping' },
      },
      {
        path: 'settings/report-formats',
        name: 'ReportFormats',
        component: () => import('@/features/reportformats/views/ReportFormatsListView.vue'),
        meta: { title: 'Report Formats' },
      },
      {
        path: 'settings/report-formats/new',
        name: 'ReportFormatCreate',
        component: () => import('@/features/reportformats/views/ReportFormatUpsertView.vue'),
        meta: { title: 'New Report Format' },
      },
      {
        path: 'settings/report-formats/:id/edit',
        name: 'ReportFormatEdit',
        component: () => import('@/features/reportformats/views/ReportFormatUpsertView.vue'),
        meta: { title: 'Edit Report Format' },
        props: true,
      },
      {
        path: 'settings/user-details',
        name: 'UserDetails',
        component: () => import('@/features/userdetails/views/UserDetailsListView.vue'),
        meta: { title: 'User Details' },
      },
      {
        path: 'settings/user-details/new',
        name: 'UserDetailCreate',
        component: () => import('@/features/userdetails/views/UserDetailUpsertView.vue'),
        meta: { title: 'Create User' },
      },
      {
        path: 'settings/user-details/:id/edit',
        name: 'UserDetailEdit',
        component: () => import('@/features/userdetails/views/UserDetailUpsertView.vue'),
        meta: { title: 'Edit User' },
        props: true,
      },
      {
        path: 'v2/accountmanagementdetail',
        redirect: (to) => {
          const raw = to.query.id
          const id = Array.isArray(raw) ? raw[0] : raw
          return id
            ? { name: 'UserDetailEdit', params: { id } }
            : { name: 'UserDetailCreate' }
        },
      },
      {
        path: 'v2/accountmanagementsearch',
        redirect: { name: 'UserDetails' },
      },
      {
        path: 'settings/user-credits',
        name: 'UserCredits',
        component: () => import('@/features/usercredits/views/UserCreditsView.vue'),
        meta: { title: 'User Credits' },
      },
      {
        // NOT '/help' — that path resolves to the legacy MVC HelpController in production.
        path: 'help-center',
        name: 'HelpCenter',
        component: () => import('@/features/help/views/HelpView.vue'),
        meta: { title: 'Help' },
      },
      {
        path: 'v2/usercredits',
        redirect: '/settings/user-credits',
      },
      {
        path: 'v2/apicredits',
        redirect: '/settings/user-credits',
      },
      {
        path: 'v2/addcredits',
        redirect: '/settings/user-credits',
      },
      {
        path: 'settings/preferred-titles',
        name: 'PreferredTitles',
        component: () => import('@/features/preferredtitles/views/PreferredTitlesListView.vue'),
        meta: { title: 'Preferred Titles' },
      },
      {
        path: 'settings/preferred-titles/new',
        name: 'PreferredTitleCreate',
        component: () => import('@/features/preferredtitles/views/PreferredTitleUpsertView.vue'),
        meta: { title: 'New Preferred Title' },
      },
      {
        path: 'settings/preferred-titles/:id/edit',
        name: 'PreferredTitleEdit',
        component: () => import('@/features/preferredtitles/views/PreferredTitleUpsertView.vue'),
        meta: { title: 'Edit Preferred Title' },
        props: true,
      },
      {
        path: 'v2/preferredtitle',
        redirect: '/settings/preferred-titles',
      },
      {
        path: 'v2/preferredtitledetail',
        redirect: (to) => {
          const raw = to.query.id
          const id = Array.isArray(raw) ? raw[0] : raw
          return id
            ? { name: 'PreferredTitleEdit', params: { id } }
            : { name: 'PreferredTitleCreate' }
        },
      },
      {
        path: 'pricing-calculator',
        name: 'PricingCalculator',
        component: () => import('@/features/pricingcalculator/views/PricingCalculatorView.vue'),
        meta: { title: 'Pricing Calculator' },
      },
      {
        path: 'sales-support-hub',
        name: 'SalesSupportHub',
        component: () => import('@/features/salessupporthub/views/SalesSupportHubView.vue'),
        meta: { title: 'Sales Support Hub' },
      },
      {
        path: 'ai-assistant',
        name: 'AiAssistant',
        component: () => import('@/features/aiassistant/views/AiAssistantView.vue'),
        // FAB + drawer are redundant on the assistant's own page; the layout-level
        // v-if also unmounts the drawer (and its watchers) here — see useAiPanel.
        // Gate direct-URL access on the datapoint too — hiding the sidebar link alone
        // left the route reachable by typing /ai-assistant.
        meta: { title: 'AI Assistant', hideAiFab: true, requiresDatapoint: 'ENABLE_AI_ASSISTANT' },
      },
      {
        path: 'subscription',
        name: 'Subscription',
        component: () => import('@/features/subscription/views/SubscriptionView.vue'),
        meta: { title: 'Subscription' },
      },
      {
        path: 'subscription/success',
        name: 'SubscriptionSuccess',
        component: () => import('@/features/subscription/views/SubscriptionSuccessView.vue'),
        meta: { title: 'Payment Successful' },
      },
      {
        // Stripe cancel_url target for V3 checkouts — the "back from Stripe" page.
        path: 'subscription/alert',
        name: 'SubscriptionAlert',
        component: () => import('@/features/subscription/views/SubscriptionAlertView.vue'),
        meta: { title: 'Subscription' },
      },
      {
        path: 'payment/update-success',
        name: 'PaymentUpdateSuccess',
        component: () => import('@/features/subscription/views/PaymentUpdateSuccessView.vue'),
        meta: { title: 'Payment Method Updated' },
      },
      {
        path: 'settings/gdpr',
        name: 'Gdpr',
        component: () => import('@/features/gdpr/views/GdprView.vue'),
        meta: { title: 'GDPR Settings' },
      },
      {
        path: 'v2/customerconsent',
        redirect: '/settings/gdpr',
      },
      {
        path: 'v2/settings/gdpr',
        redirect: '/settings/gdpr',
      },
      {
        path: 'soauthmanagement',
        redirect: '/settings/api-access',
      },
      {
        path: 'v2/settings/apiaccess',
        redirect: '/settings/api-access',
      },
      {
        path: 'v2/lookalike',
        redirect: '/lookalike',
      },
      {
        path: 'v2/salessupporthub',
        redirect: '/sales-support-hub',
      },
      {
        path: 'v2/subscription',
        redirect: '/subscription',
      },
      {
        path: 'v2/subscriptionsuccess',
        redirect: (to) => ({ path: '/subscription/success', query: to.query }),
      },
      {
        path: 'v2/paymentmethodupdatesuccess',
        redirect: '/payment/update-success',
      },
      {
        path: 'manage/memberprofile',
        redirect: '/profile',
      },
      {
        path: 'manage/changepassword',
        redirect: '/settings/change-password',
      },
    ],
  },
  {
    // SPA-native generic error page (500 / unexpected). Reachable while
    // unauthenticated; accepts optional { message, code } context via props.
    path: '/error',
    name: 'Error',
    component: () => import('@/components/common/ErrorView.vue'),
    meta: { title: 'Error', requiresAuth: false },
    props: true,
  },
  {
    // Explicit access-denied page for route-guard denials (NOT incidental API
    // 403s, which the error interceptor surfaces as a toast). Accepts optional
    // { message, code } context via props.
    path: '/access-denied',
    name: 'AccessDenied',
    component: () => import('@/components/common/AccessDeniedView.vue'),
    meta: { title: 'Access Denied', requiresAuth: false },
    props: true,
  },
  {
    // Catch-all 404. Public (requiresAuth: false) so a genuinely non-existent
    // SPA path renders the page instead of bouncing to OIDC login — matching the
    // sibling Error / AccessDenied pages. Must stay LAST so real routes win.
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/components/common/NotFoundView.vue'),
    meta: { title: 'Page Not Found', requiresAuth: false },
  },
]
